import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import ListingCard from './ListingCard';
import CreateListing from './CreateListing';
import MapView from '../../components/MapView';
import { petPolicyLabel } from '../../utils/format';

export default function Listings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filters, setFilters] = useState({ property_type: '', min_rent: '', max_rent: '', bedrooms: '', bathrooms: '', parking: '', pet_policy: '', max_distance: '' });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [saved, setSaved] = useState(new Set());
  const [tab, setTab] = useState('browse');
  const [viewMode, setViewMode] = useState('list');

  useEffect(() => { fetchListings(); }, [filters, page]);

  async function fetchListings() {
    setLoading(true);
    try {
      // Build params for the /listings API (excludes property_type='sublease')
      const params = new URLSearchParams({ page: 1, limit: 50 });
      Object.entries(filters).forEach(([k, v]) => {
        if (v && k !== 'property_type') params.append(k, v);
      });
      if (filters.property_type && filters.property_type !== 'sublease') {
        params.append('property_type', filters.property_type);
      }

      // Skip apartments fetch when filtering for subleases only, and vice versa
      const wantApartments = filters.property_type !== 'sublease';
      const wantSubleases = !filters.property_type || filters.property_type === 'sublease';

      const [listingsRes, subleasesRes] = await Promise.all([
        wantApartments ? api.get(`/listings?${params}`) : Promise.resolve({ data: { listings: [] } }),
        wantSubleases ? api.get(`/subleases?${new URLSearchParams({ page: 1, limit: 50 })}`) : Promise.resolve({ data: { subleases: [] } }),
      ]);
      const apartments = (listingsRes.data.listings || []).map(l => ({ ...l, _listingType: 'apartment' }));
      const subleases = (subleasesRes.data.subleases || []).map(s => ({ ...s, _listingType: 'sublease' }));
      const combined = [...apartments, ...subleases].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setListings(combined);
      setTotalPages(1);
    } catch {}
    setLoading(false);
  }

  async function toggleSave(listingId, listingType) {
    const base = listingType === 'sublease' ? 'subleases' : 'listings';
    try {
      if (saved.has(listingId)) {
        await api.delete(`/${base}/${listingId}/save`);
        setSaved(prev => { const s = new Set(prev); s.delete(listingId); return s; });
      } else {
        await api.post(`/${base}/${listingId}/save`);
        setSaved(prev => new Set([...prev, listingId]));
      }
    } catch {}
  }

  const [editListing, setEditListing] = useState(null);
  const [showConnectBanner, setShowConnectBanner] = useState(false);

  async function handleDelete(listingId) {
    if (!window.confirm('Are you sure you want to remove this listing?')) return;
    try {
      await api.delete(`/listings/${listingId}`);
      fetchListings();
    } catch {}
  }

  if (showCreate || editListing) {
    return <CreateListing
      existing={editListing}
      onClose={() => {
        const justPosted = showCreate && !editListing;
        setShowCreate(false);
        setEditListing(null);
        fetchListings();
        if (justPosted && !user?.stripe_onboarding_complete) {
          setShowConnectBanner(true);
        }
      }}
    />;
  }

  const isLandlord = user?.user_type === 'landlord';

  return (
    <div className="page-container">

      {/* ── Stripe Connect reminder ── */}
      {showConnectBanner && (
        <div className="flex items-start gap-4 bg-[#FDF8E8] border border-[#C8A800]/30 rounded-xl p-4 mb-6">
          <div className="w-10 h-10 rounded-full bg-[#C8A800]/15 flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg viewBox="0 0 24 24" fill="none" stroke="#C8A800" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-[#1B1B1B]">Your listing is live — now set up payments</p>
            <p className="text-sm text-[#666666] mt-0.5">Connect your bank account so tenants can pay you directly through NestHawk. Takes about 2 minutes.</p>
            <Link to="/payments?tab=connect" className="inline-block mt-3 btn-primary text-sm px-4 py-2">
              Connect Bank Account →
            </Link>
          </div>
          <button onClick={() => setShowConnectBanner(false)} className="text-[#AAAAAA] hover:text-[#555555] transition-colors flex-shrink-0">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" className="w-4 h-4">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      {/* ── Page header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-[#EBEBEB]">
        <div>
          <h1 className="section-title">Apartments &amp; Houses</h1>
          <p className="text-sm text-[#999999] nav-link mt-1">Browse rentals near the University of Iowa</p>
        </div>

        {/* Post buttons — both always visible */}
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          {/* List / Map toggle */}
          <div className="flex items-center gap-1 bg-[#F0F0F0] p-1 rounded-lg">
            {['list', 'map'].map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors nav-link ${viewMode === mode ? 'bg-white text-[#1B1B1B] shadow-sm' : 'text-[#777777] hover:text-[#1B1B1B]'}`}
              >
                {mode === 'list'
                  ? <span className="flex items-center gap-1.5"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>List</span>
                  : <span className="flex items-center gap-1.5"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>Map</span>
                }
              </button>
            ))}
          </div>
          <button
            onClick={() => navigate('/subleases', { state: { openCreate: true } })}
            className="btn-outline flex items-center gap-2"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Post a Sublease
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="btn-primary flex items-center gap-2"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Post an Apartment
          </button>
        </div>
      </div>

      {/* ── Landlord tabs ── */}
      {isLandlord && (
        <div className="flex gap-1 mb-6 bg-[#F0F0F0] p-1 rounded-lg w-fit">
          {['browse', 'my_listings'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-md text-sm font-medium capitalize transition-colors nav-link ${
                tab === t
                  ? 'bg-white border border-[#E8E8E8] shadow-sm text-[#1B1B1B]'
                  : 'text-[#777777] hover:text-[#1B1B1B]'
              }`}
            >
              {t === 'browse' ? 'Browse All' : 'My Listings'}
            </button>
          ))}
        </div>
      )}

      {/* ── Filters — hidden in map mode ── */}
      {viewMode === 'list' && (
        <div className="card p-4 mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="label text-xs">Type</label>
              <select className="input py-2 text-sm" value={filters.property_type}
                onChange={e => { setFilters({ ...filters, property_type: e.target.value }); setPage(1); }}>
                <option value="">All Types</option>
                <option value="apartment">Apartment</option>
                <option value="house">House</option>
                <option value="sublease">Sublease</option>
              </select>
            </div>
            <div>
              <label className="label text-xs">Min Rent</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA] text-sm">$</span>
                <input type="number" className="input pl-6 py-2 text-sm" placeholder="0" value={filters.min_rent}
                  onChange={e => { setFilters({ ...filters, min_rent: e.target.value }); setPage(1); }} />
              </div>
            </div>
            <div>
              <label className="label text-xs">Max Rent</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA] text-sm">$</span>
                <input type="number" className="input pl-6 py-2 text-sm" placeholder="Any" value={filters.max_rent}
                  onChange={e => { setFilters({ ...filters, max_rent: e.target.value }); setPage(1); }} />
              </div>
            </div>
            <div>
              <label className="label text-xs">Bedrooms</label>
              <select className="input py-2 text-sm" value={filters.bedrooms}
                onChange={e => { setFilters({ ...filters, bedrooms: e.target.value }); setPage(1); }}>
                <option value="">Any</option>
                {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} BR</option>)}
              </select>
            </div>
            <div>
              <label className="label text-xs">Bathrooms</label>
              <select className="input py-2 text-sm" value={filters.bathrooms}
                onChange={e => { setFilters({ ...filters, bathrooms: e.target.value }); setPage(1); }}>
                <option value="">Any</option>
                <option value="1">1+</option>
                <option value="1.5">1.5+</option>
                <option value="2">2+</option>
                <option value="3">3+</option>
              </select>
            </div>
            <div>
              <label className="label text-xs">Distance to Campus</label>
              <select className="input py-2 text-sm" value={filters.max_distance}
                onChange={e => { setFilters({ ...filters, max_distance: e.target.value }); setPage(1); }}>
                <option value="">Any</option>
                <option value="0.25">Under ¼ mile</option>
                <option value="0.5">Under ½ mile</option>
                <option value="1">Under 1 mile</option>
                <option value="2">Under 2 miles</option>
              </select>
            </div>
            <div>
              <label className="label text-xs">Parking</label>
              <select className="input py-2 text-sm" value={filters.parking}
                onChange={e => { setFilters({ ...filters, parking: e.target.value }); setPage(1); }}>
                <option value="">Any</option>
                <option value="reserved">Reserved Space</option>
                <option value="driveway">Driveway</option>
                <option value="street">Street Parking</option>
                <option value="paid">Parking Costs Extra</option>
                <option value="none">No Parking</option>
              </select>
            </div>
            <div>
              <label className="label text-xs">Pet Policy</label>
              <select className="input py-2 text-sm" value={filters.pet_policy}
                onChange={e => { setFilters({ ...filters, pet_policy: e.target.value }); setPage(1); }}>
                <option value="">Any</option>
                <option value="no_pets">No Pets</option>
                <option value="cats_only">Cats Only</option>
                <option value="dogs_allowed">Dogs OK</option>
                <option value="all_pets">All Pets</option>
              </select>
            </div>
            {Object.values(filters).some(v => v !== '') && (
              <div className="flex items-end">
                <button
                  onClick={() => { setFilters({ property_type: '', min_rent: '', max_rent: '', bedrooms: '', bathrooms: '', parking: '', pet_policy: '', max_distance: '' }); setPage(1); }}
                  className="w-full py-2 text-sm text-[#999999] hover:text-[#555555] nav-link transition-colors border border-[#E8E8E8] rounded-lg"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {viewMode === 'map' ? (
        <MapView
          listings={listings}
          pinType="mixed"
          onViewListing={(l) => navigate(l._listingType === 'sublease' ? `/subleases/${l.id}` : `/listings/${l.id}`)}
        />
      ) : loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : listings.length === 0 ? (
        <div className="text-center py-20">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-12 h-12 text-[#DDDDDD] mb-4 mx-auto">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>
          <p className="text-xl font-bold text-[#1B1B1B] mb-2">No listings found</p>
          <p className="nav-link text-[#777777]">Try adjusting your filters</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {listings.map(listing => (
              <ListingCard
                key={listing.id}
                listing={listing}
                saved={saved.has(listing.id)}
                onSave={() => toggleSave(listing.id, listing._listingType)}
                type={listing._listingType}
                onEdit={listing._listingType === 'apartment' ? () => setEditListing(listing) : undefined}
                onDelete={listing._listingType === 'apartment' ? () => handleDelete(listing.id) : undefined}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn-ghost border border-[#DDDDDD] disabled:opacity-40">
                ← Prev
              </button>
              <span className="flex items-center px-4 nav-link text-[#777777] text-sm">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-ghost border border-[#DDDDDD] disabled:opacity-40">
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
